import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store/useStore';
import { isWeekend } from '@mui/x-scheduler-headless/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { getDayList } from '@mui/x-scheduler-headless/get-day-list';
import { eventTimelinePremiumViewSelectors } from '@mui/x-scheduler-headless-premium/event-timeline-premium-selectors';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-headless-premium/use-event-timeline-premium-store-context';
import { formatWeekDayMonthAndDayOfMonth } from '@mui/x-scheduler/internals';
import { useEventTimelinePremiumStyledContext } from "../../EventTimelinePremiumStyledContext.mjs";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const WeeksHeaderRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'WeeksHeader'
})({
  display: 'flex',
  // TODO: update this calculation when we add the option to hide weekends
  minWidth: 'calc(var(--unit-count) * var(--weeks-cell-width))'
});
const TimeHeaderCell = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'WeeksHeaderCell'
})(({
  theme
}) => ({
  flexGrow: 1,
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  '&:not(:last-child)': {
    borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
const DayLabel = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'WeeksHeaderDayLabel'
})(({
  theme
}) => ({
  padding: theme.spacing(1),
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
}));
const WeekDaysRow = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'WeeksHeaderDaysRow'
})({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, var(--time-cell-width))'
});
const WeekDayCell = styled('time', {
  name: 'MuiEventTimeline',
  slot: 'WeeksHeaderDayCell'
})(({
  theme
}) => ({
  padding: theme.spacing(0.5),
  textAlign: 'center',
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  '&[data-weekend]': {
    color: (theme.vars || theme).palette.error.main
  },
  '&:not(:last-child)': {
    borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
  }
}));
export function WeeksHeader(props) {
  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();
  const {
    classes
  } = useEventTimelinePremiumStyledContext();

  // Selector hooks
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);

  // Feature hooks
  const weeks = React.useMemo(() => {
    const days = getDayList({
      adapter,
      start: viewConfig.start,
      end: adapter.endOfWeek(viewConfig.end)
    });
    const tempWeeks = [];
    let weekNumber = null;
    for (const day of days) {
      const lastWeek = tempWeeks[tempWeeks.length - 1];
      const dayWeekNumber = adapter.getWeekNumber(day.value);
      if (weekNumber !== dayWeekNumber) {
        weekNumber = dayWeekNumber;
        tempWeeks.push([day]);
      } else {
        lastWeek.push(day);
      }
    }
    return tempWeeks;
  }, [adapter, viewConfig]);
  return /*#__PURE__*/_jsx(WeeksHeaderRoot, _extends({
    className: classes.weeksHeader
  }, props, {
    children: weeks.map(week => /*#__PURE__*/_jsxs(TimeHeaderCell, {
      className: classes.weeksHeaderCell,
      children: [/*#__PURE__*/_jsxs(DayLabel, {
        className: classes.weeksHeaderDayLabel,
        children: [formatWeekDayMonthAndDayOfMonth(week[0].value, adapter), " -", ' ', formatWeekDayMonthAndDayOfMonth(week[6].value, adapter)]
      }), /*#__PURE__*/_jsx(WeekDaysRow, {
        className: classes.weeksHeaderDaysRow,
        children: week.map(day => /*#__PURE__*/_jsx(WeekDayCell, {
          dateTime: day.key,
          className: classes.weeksHeaderDayCell,
          "data-weekend": isWeekend(adapter, day.value) ? '' : undefined,
          children: adapter.format(day.value, 'weekday1Letter')
        }, day.key))
      })]
    }, `${week[0].key}-week`))
  }));
}